import React from 'react';

const SNAP_THRESHOLD = 5;
const GUIDE_COLOR = '#999';

interface FieldRect {
  screenX: number;
  screenY: number;
  screenW: number;
  screenH: number;
}

export interface SnapResult {
  snapX: number | null;
  snapY: number | null;
}

export function calculateSnap(
  dragged: FieldRect,
  others: FieldRect[],
): { snap: SnapResult; guides: { x: number | null; y: number | null } } {
  const dragLeft = dragged.screenX;
  const dragRight = dragged.screenX + dragged.screenW;
  const dragCenterX = dragged.screenX + dragged.screenW / 2;
  const dragTop = dragged.screenY;
  const dragBottom = dragged.screenY + dragged.screenH;
  const dragCenterY = dragged.screenY + dragged.screenH / 2;

  let snapX: number | null = null;
  let snapY: number | null = null;
  let guideX: number | null = null;
  let guideY: number | null = null;
  let bestDx = SNAP_THRESHOLD + 1;
  let bestDy = SNAP_THRESHOLD + 1;

  for (const o of others) {
    const oLeft = o.screenX;
    const oRight = o.screenX + o.screenW;
    const oCenterX = o.screenX + o.screenW / 2;
    const oTop = o.screenY;
    const oBottom = o.screenY + o.screenH;
    const oCenterY = o.screenY + o.screenH / 2;

    const xChecks = [
      { dragEdge: dragLeft, otherEdge: oLeft, guide: oLeft },
      { dragEdge: dragRight, otherEdge: oRight, guide: oRight },
      { dragEdge: dragCenterX, otherEdge: oCenterX, guide: oCenterX },
      { dragEdge: dragLeft, otherEdge: oRight, guide: oRight },
      { dragEdge: dragRight, otherEdge: oLeft, guide: oLeft },
    ];
    for (const { dragEdge, otherEdge, guide } of xChecks) {
      const d = Math.abs(dragEdge - otherEdge);
      if (d < bestDx) {
        bestDx = d;
        snapX = dragged.screenX + (otherEdge - dragEdge);
        guideX = guide;
      }
    }

    const yChecks = [
      { dragEdge: dragTop, otherEdge: oTop, guide: oTop },
      { dragEdge: dragBottom, otherEdge: oBottom, guide: oBottom },
      { dragEdge: dragCenterY, otherEdge: oCenterY, guide: oCenterY },
      { dragEdge: dragTop, otherEdge: oBottom, guide: oBottom },
      { dragEdge: dragBottom, otherEdge: oTop, guide: oTop },
    ];
    for (const { dragEdge, otherEdge, guide } of yChecks) {
      const d = Math.abs(dragEdge - otherEdge);
      if (d < bestDy) {
        bestDy = d;
        snapY = dragged.screenY + (otherEdge - dragEdge);
        guideY = guide;
      }
    }
  }

  if (bestDx > SNAP_THRESHOLD) {
    snapX = null;
    guideX = null;
  }
  if (bestDy > SNAP_THRESHOLD) {
    snapY = null;
    guideY = null;
  }

  return { snap: { snapX, snapY }, guides: { x: guideX, y: guideY } };
}

interface PdfAlignmentGuidesProps {
  guides: { x: number | null; y: number | null };
  canvasWidth: number;
  canvasHeight: number;
}

const PdfAlignmentGuides: React.FC<PdfAlignmentGuidesProps> = ({
  guides,
  canvasWidth,
  canvasHeight,
}) => {
  return (
    <>
      {guides.x !== null && (
        <div
          style={{
            position: 'absolute',
            left: `${guides.x}px`,
            top: 0,
            width: '1px',
            height: `${canvasHeight}px`,
            borderLeft: `1px dashed ${GUIDE_COLOR}`,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}
      {guides.y !== null && (
        <div
          style={{
            position: 'absolute',
            top: `${guides.y}px`,
            left: 0,
            height: '1px',
            width: `${canvasWidth}px`,
            borderTop: `1px dashed ${GUIDE_COLOR}`,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}
    </>
  );
};

export default PdfAlignmentGuides;
